import { useMemo } from 'react';

import { useFeatureGate } from '@trello/feature-gate-client';

export interface PlannerFeatureGates {
  isZenModeEnabled: boolean;
  isAutoUpdatingDateEnabled: boolean;
  isSingleCardEventEnabled: boolean;
  isPlannerCardActionsEnabled: boolean;
  isPlannerOutlookEnabled: boolean;
  isPlannerEventPreloadingEnabled: boolean;
  isScheduleFromCardQuickActionsEnabled: boolean;
  isTemporaryPlannerEventPreviewEnabled: boolean;
  isSimplerAdjustViewEnabled: boolean;
  isUseLoginHintEnabled: boolean;
  isMultiAccountEnabled: boolean;
  isCustomOrderEnabled: boolean;
}

/**
 * Hook to determine access to various planner features.
 *
 * @returns {PlannerFeatureGates} An object containing boolean flags for different planner features
 */
export function usePlannerFeatureGate(): PlannerFeatureGates {
  const { value: useLoginHint } = useFeatureGate(
    'trello_electric_use_login_hint',
  );
  const { value: autoUpdatingDateGate } = useFeatureGate(
    'xf_planner_auto_updating_date',
  );
  const { value: plannerEventPreloading } = useFeatureGate(
    'xf_planner_event_preloading',
  );
  const { value: plannerOutlook } = useFeatureGate('xf_planner_outlook');
  const { value: plannerCardActions } = useFeatureGate(
    'xf_planner_card_actions',
  );

  const { value: scheduleFromCardQuickActions } = useFeatureGate(
    'xf_planner_card_quick_actions_scheduler',
  );
  const { value: singleCardEventGate } = useFeatureGate(
    'xf_planner_single_card_event',
  );
  const { value: temporaryPlannerEventPreview } = useFeatureGate(
    'xf_planner_event_preview_temporary',
  );
  const { value: plannerSimplerAdjustView } = useFeatureGate(
    'xf_planner_simpler_adjust_view',
  );
  const { value: zenModeGate } = useFeatureGate('phx_zen_mode');
  const { value: multiAccountGate } = useFeatureGate('electric_multi_account');
  const { value: isCustomOrderEnabled } = useFeatureGate(
    'phx_planner_custom_order',
  );

  return useMemo(
    () => ({
      isZenModeEnabled: zenModeGate,
      isSingleCardEventEnabled: singleCardEventGate,
      isAutoUpdatingDateEnabled: autoUpdatingDateGate,
      isPlannerOutlookEnabled: plannerOutlook,
      isPlannerEventPreloadingEnabled: plannerEventPreloading,
      isScheduleFromCardQuickActionsEnabled: scheduleFromCardQuickActions,
      isTemporaryPlannerEventPreviewEnabled: temporaryPlannerEventPreview,
      isPlannerCardActionsEnabled: plannerCardActions,
      isSimplerAdjustViewEnabled: plannerSimplerAdjustView,
      isUseLoginHintEnabled: useLoginHint,
      isMultiAccountEnabled: multiAccountGate,
      isCustomOrderEnabled,
    }),
    [
      zenModeGate,
      singleCardEventGate,
      autoUpdatingDateGate,
      plannerOutlook,
      plannerEventPreloading,
      scheduleFromCardQuickActions,
      temporaryPlannerEventPreview,
      plannerCardActions,
      plannerSimplerAdjustView,
      useLoginHint,
      multiAccountGate,
      isCustomOrderEnabled,
    ],
  );
}
